var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
import { useCallback, useEffect, useMemo, useState } from 'react';
import { PAGE_SIZE } from '../dataGrid';
var DEBOUNCE_MS = 300;
// Debounces a value so rapid changes (typing, checkbox toggles) coalesce into one refetch.
function useDebouncedValue(value, delay) {
  var _a = useState(value),
    debounced = _a[0],
    setDebounced = _a[1];
  useEffect(function () {
    var id = setTimeout(function () {
      return setDebounced(value);
    }, delay);
    return function () {
      return clearTimeout(id);
    };
  }, [value, delay]);
  return debounced;
}
/**
 * Owns server-side table state (sort, per-column filters, pagination) for any resource, and emits the
 * query params to send. It does not hold the rows: the caller fetches with `queryParams` and feeds the
 * response's `facets`/`total` back in. Filter option lists come from `facets`, labeled via the column's
 * `getLabel`. See ColumnFilterHeader for the header UI that consumes `getFilterState`/`applyFilter`.
 */
export function useServerTableFilter(_a) {
  var columns = _a.columns,
    facets = _a.facets,
    rowCount = _a.rowCount,
    _b = _a.pageSize,
    pageSize = _b === void 0 ? PAGE_SIZE : _b,
    defaultSort = _a.defaultSort,
    search = _a.search;
  var _c = useState(defaultSort !== null && defaultSort !== void 0 ? defaultSort : null),
    sort = _c[0],
    setSort = _c[1];
  var _d = useState({}),
    filters = _d[0],
    setFilters = _d[1];
  var _e = useState(0),
    page = _e[0],
    setPage = _e[1];
  var configByField = useMemo(function () {
    var map = {};
    columns.forEach(function (col) {
      return map[col.field] = col;
    });
    return map;
  }, [columns]);
  // Reset to the first page whenever the external search term changes.
  useEffect(function () {
    return setPage(0);
  }, [search]);
  var applyFilter = useCallback(function (field, update) {
    if ('sort' in update) {
      // Only one column sorts at a time.
      setSort(update.sort ? {
        field: field,
        order: update.sort
      } : function (prev) {
        return (prev === null || prev === void 0 ? void 0 : prev.field) === field ? null : prev;
      });
    }
    if ('selectedValues' in update || 'contains' in update || 'dateRange' in update) {
      setFilters(function (prev) {
        var _a;
        var next = __assign(__assign({}, prev), (_a = {}, _a[field] = __assign({}, prev[field]), _a));
        if ('selectedValues' in update) next[field].selectedValues = update.selectedValues;
        if ('contains' in update) next[field].contains = update.contains;
        if ('dateRange' in update) next[field].dateRange = update.dateRange;
        return next;
      });
    }
    setPage(0);
  }, []);
  var resetFilters = useCallback(function () {
    setFilters({});
    setSort(defaultSort !== null && defaultSort !== void 0 ? defaultSort : null);
    setPage(0);
  }, [defaultSort]);
  // Serialize the query-affecting state, then debounce so typing/toggling doesn't refetch per keystroke.
  var querySignature = useMemo(function () {
    return JSON.stringify({
      sort: sort,
      filters: filters,
      search: (search === null || search === void 0 ? void 0 : search.trim()) || undefined,
      page: page,
      pageSize: pageSize
    });
  }, [sort, filters, search, page, pageSize]);
  var debouncedSignature = useDebouncedValue(querySignature, DEBOUNCE_MS);
  var queryParams = useMemo(function () {
    var state = JSON.parse(debouncedSignature);
    var activeFilters = Object.entries(state.filters).map(function (_a) {
      var _b;
      var field = _a[0],
        value = _a[1];
      if ((_b = value.selectedValues) === null || _b === void 0 ? void 0 : _b.length) return {
        field: field,
        values: value.selectedValues
      };
      if (value.contains) return {
        field: field,
        contains: value.contains
      };
      if (value.dateRange && (value.dateRange.from || value.dateRange.to)) {
        return __assign(__assign({
          field: field
        }, value.dateRange.from ? {
          from: value.dateRange.from
        } : {}), value.dateRange.to ? {
          to: value.dateRange.to
        } : {});
      }
      return null;
    }).filter(function (f) {
      return f !== null;
    });
    return __assign(__assign(__assign({
      pageNumber: state.page + 1,
      pageSize: state.pageSize
    }, state.sort ? {
      sortColumn: state.sort.field,
      sortOrder: state.sort.order.toUpperCase()
    } : {}), state.search ? {
      search: state.search
    } : {}), activeFilters.length ? {
      filters: JSON.stringify(activeFilters)
    } : {});
  }, [debouncedSignature]);
  var sortModel = useMemo(function () {
    return sort ? [{
      field: sort.field,
      sort: sort.order
    }] : [];
  }, [sort]);
  var paginationModel = useMemo(function () {
    return {
      page: page,
      pageSize: pageSize
    };
  }, [page, pageSize]);
  var onPaginationModelChange = useCallback(function (model) {
    return setPage(model.page);
  }, []);
  var getFilterState = useCallback(function (field) {
    var _a, _b, _c;
    var config = configByField[field];
    var state = filters[field];
    return {
      sort: (sort === null || sort === void 0 ? void 0 : sort.field) === field ? sort.order : null,
      selectedValues: (_a = state === null || state === void 0 ? void 0 : state.selectedValues) !== null && _a !== void 0 ? _a : null,
      contains: (_b = state === null || state === void 0 ? void 0 : state.contains) !== null && _b !== void 0 ? _b : null,
      dateRange: (_c = state === null || state === void 0 ? void 0 : state.dateRange) !== null && _c !== void 0 ? _c : null,
      kind: config === null || config === void 0 ? void 0 : config.filter,
      sortable: config === null || config === void 0 ? void 0 : config.sortable
    };
  }, [configByField, filters, sort]);
  var getAllValues = useCallback(function (field) {
    var _a;
    var config = configByField[field];
    return ((_a = facets === null || facets === void 0 ? void 0 : facets[field]) !== null && _a !== void 0 ? _a : []).map(function (_a) {
      var _b, _c;
      var value = _a.value,
        count = _a.count;
      return {
        value: value,
        label: (_c = (_b = config === null || config === void 0 ? void 0 : config.getLabel) === null || _b === void 0 ? void 0 : _b.call(config, value)) !== null && _c !== void 0 ? _c : value,
        count: count
      };
    });
  }, [configByField, facets]);
  return {
    queryParams: queryParams,
    sortModel: sortModel,
    paginationModel: paginationModel,
    onPaginationModelChange: onPaginationModelChange,
    rowCount: rowCount,
    getFilterState: getFilterState,
    getAllValues: getAllValues,
    applyFilter: applyFilter,
    resetFilters: resetFilters
  };
}